import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "../components/Modal";
import {
  User,
  Mail,
  Building2,
  Phone,
  MapPin,
  Bell,
  Moon,
  Globe,
  LogOut,
  ChevronRight,
  Shield,
  HelpCircle,
  FileText,
  Edit2,
  Camera,
  Check,
  X,
  Image as ImageIcon,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { authAPI } from "../services/api";
import "./Profile.css";

function Profile() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { user, logout } = useAuth();
  const { showToast } = useApp();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    notifications: true,
    darkMode: false,
    language: "en",
  });

  // Camera & Modal States
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);

  const fileInputRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  const handleAvatarClick = () => {
    setShowPhotoOptions(true);
  };

  const handleGalleryClick = () => {
    setShowPhotoOptions(false);
    fileInputRef.current?.click();
  };

  const startCamera = async () => {
    setShowPhotoOptions(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 640 },
        },
      });
      setCameraStream(stream);
      setIsCameraOpen(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      showToast("Could not access camera", "error");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  const uploadFile = async (file) => {
    if (!file) return;

    // Validate type
    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file", "error");
      return;
    }

    // Validate size (e.g., 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size must be less than 5MB", "error");
      return;
    }

    try {
      showToast("Uploading photo...", "info");
      const response = await authAPI.uploadAvatar(file);
      const newAvatarUrl = response?.data?.avatar_url;

      if (newAvatarUrl) {
        setProfile((prev) => ({ ...prev, avatar: newAvatarUrl }));
        showToast("Profile photo updated!", "success");
      }
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      showToast("Failed to upload photo", "error");
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Target a square crop from the center
      const size = Math.min(video.videoWidth, video.videoHeight);
      const startX = (video.videoWidth - size) / 2;
      const startY = (video.videoHeight - size) / 2;

      canvas.width = 400;
      canvas.height = 400;

      context.drawImage(video, startX, startY, size, size, 0, 0, 400, 400);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], "profile-photo.jpg", {
            type: "image/jpeg",
          });
          uploadFile(file);
          stopCamera();
        },
        "image/jpeg",
        0.9,
      );
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    uploadFile(file);
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      const data = response.data;

      console.log("Profile API Response:", data);
      console.log("Phone from API:", data.phone);

      setProfile({
        id: data.id,
        name: data.full_name,
        email: data.email,
        phone: data.phone,
        organization: data.organization || "",
        designation: data.job_title || "",
        city: "Mumbai",
        passType: data.pass_type || "Standard",
        registrationId: data.registration_id || "N/A",
        avatar: data.avatar_url || user?.avatar_url,
      });
      if (data.preferences) {
        setPreferences(data.preferences);
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      // Demo data fallback
      setProfile({
        id: user?.id || "att-001",
        name: user?.full_name || user?.name || "User Name",
        email: user?.email || "user@example.com",
        phone: "",
        organization: user?.organization || "Organization",
        designation: user?.job_title || "",
        city: "",
        passType: "Standard",
        registrationId: "PENDING",
        avatar: user?.avatar_url || null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || "");
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue("");
  };

  const handleEditSave = async () => {
    if (!editValue.trim()) {
      showToast("Field cannot be empty", "error");
      return;
    }

    // Map frontend fields to backend schema
    const fieldMapping = {
      name: "full_name",
      designation: "job_title",
      organization: "organization",
      phone: "phone",
      avatar: "avatar_url",
    };

    const backendField = fieldMapping[editingField];

    if (!backendField) {
      showToast("This field cannot be updated", "error");
      setEditingField(null);
      return;
    }

    setSaving(true);
    try {
      await authAPI.updateProfile({ [backendField]: editValue.trim() });
      setProfile((prev) => ({ ...prev, [editingField]: editValue.trim() }));
      showToast("Profile updated", "success");
      setEditingField(null);
      setEditValue("");
    } catch (error) {
      console.error("Failed to update profile:", error);
      showToast("Failed to update profile", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePreferenceToggle = async (key) => {
    const newValue = !preferences[key];
    setPreferences((prev) => ({ ...prev, [key]: newValue }));

    try {
      await authAPI.updatePreferences({ [key]: newValue });
      showToast("Preference updated", "success");
    } catch (error) {
      console.error("Failed to update preference:", error);
      // Revert on error
      setPreferences((prev) => ({ ...prev, [key]: !newValue }));
      showToast("Failed to update preference", "error");
    }
  };

  // Logout Modal State
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      showToast("Logout failed", "error");
    } finally {
      setShowLogoutConfirm(false);
    }
  };

  const renderEditableField = (
    field,
    label,
    value,
    icon,
    isEditable = true,
  ) => {
    if (field === "phone") {
      console.log(
        "Rendering phone field with value:",
        value,
        "Type:",
        typeof value,
      );
    }
    const isEditing = editingField === field;

    return (
      <div className="profile-field">
        <div className="field-icon">{icon}</div>
        <div className="field-content">
          <span className="field-label">{label}</span>
          {isEditing ? (
            <div className="field-edit">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="field-input"
                autoFocus
                disabled={saving}
              />
              <button
                className="edit-btn save"
                onClick={handleEditSave}
                disabled={saving}
              >
                <Check size={16} />
              </button>
              <button
                className="edit-btn cancel"
                onClick={handleEditCancel}
                disabled={saving}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="field-value-row">
              <span className="field-value">{value || "Not set"}</span>
              {isEditable && (
                <button
                  className="edit-trigger"
                  onClick={() => handleEditStart(field, value)}
                >
                  <Edit2 size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-skeleton">
          <div className="skeleton avatar-skeleton"></div>
          <div className="skeleton name-skeleton"></div>
          <div className="skeleton email-skeleton"></div>
          <div className="skeleton card-skeleton"></div>
          <div className="skeleton card-skeleton"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="avatar-container">
          {profile.avatar ? (
            <img
              src={profile.avatar}
              alt={profile.name}
              className="avatar-image"
            />
          ) : (
            <div className="avatar-placeholder">
              <User size={40} />
            </div>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            accept="image/*"
          />
          <button className="avatar-edit-btn" onClick={handleAvatarClick}>
            <Camera size={16} />
          </button>
        </div>
        <h1 className="profile-name">{profile.name}</h1>
        <p className="profile-designation">{profile.designation}</p>
        <div className="profile-badge">
          <Shield size={14} />
          <span>{profile.passType}</span>
        </div>
      </div>

      {/* Registration Info */}
      <div className="profile-section">
        <div className="registration-card">
          <div className="reg-info">
            <span className="reg-label">Registration ID</span>
            <span className="reg-value">{profile.registrationId}</span>
          </div>
          <button
            className="qr-btn"
            onClick={() =>
              navigate(eventId ? `/event/${eventId}/my-qr` : "/my-qr")
            }
          >
            View QR Code
          </button>
        </div>
      </div>

      {/* Personal Information */}
      <div className="profile-section">
        <h2 className="section-title">Personal Information</h2>
        <div className="profile-card">
          {renderEditableField(
            "name",
            "Full Name",
            profile.name,
            <User size={18} />,
          )}
          <div className="field-divider"></div>
          {renderEditableField(
            "email",
            "Email",
            profile.email,
            <Mail size={18} />,
            false,
          )}
          <div className="field-divider"></div>
          {renderEditableField(
            "phone",
            "Phone",
            profile.phone,
            <Phone size={18} />,
          )}
          <div className="field-divider"></div>
          {renderEditableField(
            "organization",
            "Organization",
            profile.organization,
            <Building2 size={18} />,
          )}
          <div className="field-divider"></div>
          {renderEditableField(
            "city",
            "City",
            profile.city,
            <MapPin size={18} />,
            false,
          )}
        </div>
      </div>

      {/* Preferences */}
      <div className="profile-section">
        <h2 className="section-title">Preferences</h2>
        <div className="profile-card">
          <div className="preference-item">
            <div className="pref-icon">
              <Bell size={18} />
            </div>
            <div className="pref-content">
              <span className="pref-label">Push Notifications</span>
              <span className="pref-desc">
                Receive session reminders and updates
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.notifications}
                onChange={() => handlePreferenceToggle("notifications")}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div className="field-divider"></div>
          <div className="preference-item">
            <div className="pref-icon">
              <Moon size={18} />
            </div>
            <div className="pref-content">
              <span className="pref-label">Dark Mode</span>
              <span className="pref-desc">Coming soon</span>
            </div>
            <label className="toggle-switch disabled">
              <input type="checkbox" checked={preferences.darkMode} disabled />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div className="field-divider"></div>
          <div className="preference-item">
            <div className="pref-icon">
              <Globe size={18} />
            </div>
            <div className="pref-content">
              <span className="pref-label">Language</span>
              <span className="pref-desc">English</span>
            </div>
            <ChevronRight size={18} className="pref-arrow" />
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="profile-section">
        <h2 className="section-title">Support</h2>
        <div className="profile-card">
          <button
            className="menu-item"
            onClick={() =>
              navigate(eventId ? `/event/${eventId}/help` : "/help")
            }
          >
            <div className="menu-icon">
              <HelpCircle size={18} />
            </div>
            <span className="menu-label">Help & Support</span>
            <ChevronRight size={18} className="menu-arrow" />
          </button>
          <div className="field-divider"></div>
          <button
            className="menu-item"
            onClick={() =>
              navigate(eventId ? `/event/${eventId}/survey` : "/survey")
            }
          >
            <div className="menu-icon">
              <FileText size={18} />
            </div>
            <span className="menu-label">Feedback Survey</span>
            <ChevronRight size={18} className="menu-arrow" />
          </button>
        </div>
      </div>

      {user?.is_admin && (
        <div className="profile-section">
          <h2 className="section-title">Administration</h2>
          <div className="profile-card">
            <button className="menu-item" onClick={() => navigate("/admin")}>
              <div className="menu-icon" style={{ color: "#E85D04" }}>
                <Shield size={18} />
              </div>
              <span className="menu-label">Admin Panel</span>
              <ChevronRight size={18} className="menu-arrow" />
            </button>
          </div>
        </div>
      )}

      {/* Logout */}
      <div className="profile-section">
        <button className="logout-btn" onClick={handleLogout}>
          <LogOut size={18} />
          <span>Log Out</span>
        </button>
      </div>

      {/* Photo Selection Modal */}
      <Modal
        isOpen={showPhotoOptions}
        onClose={() => setShowPhotoOptions(false)}
        title="Change Profile Photo"
      >
        <div
          className="photo-options-content"
          style={{ display: "flex", gap: "16px", padding: "16px" }}
        >
          <button
            className="btn btn-outline"
            onClick={startCamera}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}
          >
            <Camera size={32} />
            <span>Take Photo</span>
          </button>
          <button
            className="btn btn-outline"
            onClick={handleGalleryClick}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}
          >
            <ImageIcon size={32} />
            <span>Choose from Gallery</span>
          </button>
        </div>
      </Modal>

      {/* Camera Capture Modal */}
      <Modal isOpen={isCameraOpen} onClose={stopCamera} title="Take Photo">
        <div
          className="camera-modal-content"
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div
            className="video-container"
            style={{
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#000",
            }}
          >
            <video
              ref={(el) => {
                videoRef.current = el;
                if (el && cameraStream) {
                  el.srcObject = cameraStream;
                  el.play().catch((e) =>
                    console.error("Error playing video:", e),
                  );
                }
              }}
              autoPlay
              playsInline
              muted
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <div
            className="camera-modal-actions"
            style={{ display: "flex", gap: "12px" }}
          >
            <button
              type="button"
              className="btn btn-ghost"
              onClick={stopCamera}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={capturePhoto}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: "#E85D04",
                color: "white",
                border: "none",
              }}
            >
              Capture
            </button>
          </div>
        </div>
      </Modal>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title="Confirm Logout"
      >
        <div style={{ padding: "10px 0", color: "var(--rf-gray-700)" }}>
          Are you sure you want to log out?
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            marginTop: "20px",
          }}
        >
          <button
            className="btn btn-ghost"
            onClick={() => setShowLogoutConfirm(false)}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={confirmLogout}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              backgroundColor: "#E85D04",
              color: "white",
              border: "none",
            }}
          >
            Log Out
          </button>
        </div>
      </Modal>

      {/* App Version */}
      <div className="app-version">
        <p>RF Conference App v1.0.0</p>
        <p>© 2026 RF Foundation</p>
      </div>
    </div>
  );
}

export default Profile;
